<template>
    <a
        data-spec-class="sidebar-menu-item__link"
        :href="href"
        :class="linkClasses"
        :target="isExternal ? '_blank' : undefined"
        :rel="isExternal ? 'noopener noreferrer' : undefined"
        @click="handleClick"
    >
        <component
            :is="icon"
            class="h-6 w-6 shrink-0"
            :class="iconClasses"
            aria-hidden="true"
        />
        <span
            data-spec-class="sidebar-menu-item__label"
            class="flex-1"
        >
            <slot />
        </span>
        <div
            v-if="$slots.trailing"
            data-spec-class="sidebar-menu-item__trailing"
            class="ml-auto"
        >
            <slot name="trailing" />
        </div>
    </a>
</template>

<script lang="ts" setup>
import type { Component } from 'vue';
import { computed } from 'vue';

interface Props {
    href: string;
    icon: Component;
    isActive?: boolean;
    isExternal?: boolean;
    navigate?: (e?: MouseEvent) => void | Promise<unknown>;
}

const props = withDefaults(defineProps<Props>(), {
    isActive: false,
    isExternal: false,
    navigate: undefined,
});

const handleClick = (e: MouseEvent) => {
    if (props.isExternal) {
        return;
    }
    props.navigate?.(e);
};

const linkClasses = computed(() => {
    const base =
        'group flex items-center rounded-md px-3 py-3 text-base font-medium transition duration-300 ease-in-out md:text-sm';
    const state = props.isActive
        ? 'bg-gray-900 text-white'
        : 'text-gray-300 hover:bg-gray-700 hover:text-white';
    return [base, state];
});

/** Heroicons are functional components (functions); custom icons are Vue SFCs (objects). */
const isHeroicon = computed(() => typeof props.icon === 'function');

const iconClasses = computed(() => {
    const color = !isHeroicon.value
        ? props.isActive
            ? 'fill-gray-300'
            : 'fill-gray-400 group-hover:fill-gray-300'
        : props.isActive
          ? 'stroke-gray-300'
          : 'stroke-gray-400 group-hover:stroke-gray-300';
    return ['mr-3', color];
});
</script>
